import React, { useState, useEffect } from 'react';
import { MessageCircle, X } from 'lucide-react';

const WhatsAppButton = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);

  const WHATSAPP_NUMBER = '524422585560';
  const DEFAULT_MESSAGE = 'Hola, me interesa información sobre sus perfumes';
  const whatsappUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(DEFAULT_MESSAGE)}`;

  useEffect(() => {
    // Show button after a short delay for smooth entrance
    const timer = setTimeout(() => setIsVisible(true), 1000);
    
    // Show tooltip after 3 seconds if user hasn't interacted
    const tooltipTimer = setTimeout(() => {
      if (!hasInteracted) {
        setShowTooltip(true);
        // Hide tooltip after 5 seconds
        setTimeout(() => setShowTooltip(false), 5000);
      }
    }, 3000);

    return () => {
      clearTimeout(timer);
      clearTimeout(tooltipTimer);
    };
  }, [hasInteracted]);

  const handleClick = () => {
    setHasInteracted(true);
    setShowTooltip(false);
    window.open(whatsappUrl, '_blank');
  };

  if (!isVisible) return null;

  return (
    <>
      {/* WhatsApp Floating Button */}
      <div className="fixed bottom-20 right-4 z-[55] sm:bottom-6 sm:right-6">
        {/* Tooltip bubble — anclada a la derecha y crece hacia la izquierda, lejos del borde de la pantalla */}
        {showTooltip && (
          <div className="absolute bottom-full right-0 mb-3 animate-fade-in">
            <div className="relative bg-white text-brand-black px-4 py-3 rounded-2xl shadow-luxury-lg w-[220px]">
              <button
                onClick={() => setShowTooltip(false)}
                className="absolute -top-2 -right-2 w-5 h-5 bg-brand-black/10 rounded-full flex items-center justify-center hover:bg-brand-black/20 transition-colors"
                aria-label="Cerrar"
              >
                <X size={12} />
              </button>
              <p className="text-sm font-medium leading-snug">¿Tienes dudas? 💬</p>
              <p className="text-xs text-brand-black/60 mt-1 leading-snug">¡Escríbenos por WhatsApp!</p>
              {/* Arrow */}
              <div className="absolute -bottom-2 right-6 w-4 h-4 bg-white transform rotate-45" />
            </div>
          </div>
        )}

        {/* Main Button */}
        <button
          onClick={handleClick}
          onMouseEnter={() => !hasInteracted && setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
          className="group relative flex items-center justify-center"
          aria-label="Contactar por WhatsApp"
        >
          {/* Pulse ring animation */}
          <div className="absolute inset-0 bg-[#25D366] rounded-full animate-ping opacity-25" />
          
          {/* Outer glow ring */}
          <div className="absolute inset-[-4px] bg-gradient-to-r from-[#25D366] to-[#128C7E] rounded-full opacity-30 blur-sm group-hover:opacity-50 transition-opacity" />
          
          {/* Main button */}
          <div className="relative w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-br from-[#25D366] to-[#128C7E] rounded-full flex items-center justify-center shadow-lg shadow-[#25D366]/30 group-hover:shadow-[#25D366]/50 group-hover:scale-110 transition-all duration-300">
            {/* WhatsApp Icon */}
            <svg 
              viewBox="0 0 24 24" 
              className="w-7 h-7 sm:w-8 sm:h-8 text-white fill-current"
            >
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
          </div>

          {/* Online indicator dot */}
          <div className="absolute top-0 right-0 w-4 h-4 sm:w-5 sm:h-5 bg-white rounded-full flex items-center justify-center shadow-md">
            <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 bg-[#25D366] rounded-full animate-pulse" />
          </div>
        </button>
      </div>

      {/* Animation styles */}
      <style>{`
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fade-in {
          animation: fade-in 0.3s ease-out forwards;
        }
      `}</style>
    </>
  );
};

export default WhatsAppButton;
